/**
 * 📤 DemoShareCluster — Boutons de partage social pour la scène finale de la démo.
 * Source unique : /api/public/demo/share-meta (backend-driven).
 * Tracking anonymisé : POST /api/public/demo/share-event.
 *
 * Zéro fallback : si l'API est indisponible, les boutons sont désactivés
 * avec un tooltip explicite. Aucun partage muet.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Link as LinkIcon, Check, Loader2, Share2 } from 'lucide-react';
import { T } from '../HeroDemoVideo';

const API = process.env.REACT_APP_BACKEND_URL;

const ShareBtn = ({ icon: Icon, label, onClick, disabled, copied, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-9 rounded-full text-[12px] font-semibold transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
    style={{
      background: copied ? T.green : 'rgba(255,255,255,0.92)',
      color: copied ? '#FFFFFF' : T.ink,
      border: `1px solid ${copied ? T.green : T.gold}`,
    }}
  >
    {copied ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
    <span className="hidden sm:inline">{copied ? 'Lien copié' : label}</span>
  </button>
);

export default function DemoShareCluster() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/public/demo/share-meta`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Erreur ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setMeta(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Erreur réseau');
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const trackEvent = useCallback(async (channel) => {
    try {
      await fetch(`${API}/api/public/demo/share-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, scene_id: 'transformation' }),
      });
    } catch (_) { /* tracking best-effort */ }
  }, []);

  const openExternal = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=720,height=620');
  }, []);

  // 📱 Native share API mobile (iOS / Android) — priorité absolue si disponible
  const onNativeShare = useCallback(async () => {
    if (!meta || typeof navigator === 'undefined' || !navigator.share) return false;
    try {
      await navigator.share({
        title: 'CoachDIA — Leadership mesurable',
        text: meta.text_linkedin || meta.text_x,
        url: meta.channels?.copy?.url || meta.base_url,
      });
      trackEvent('native');
      return true;
    } catch (e) {
      // User dismissed: AbortError → on retombe simplement sans erreur visible.
      if (e?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn('[DemoShare] navigator.share error:', e?.name);
      }
      return false;
    }
  }, [meta, trackEvent]);

  const hasNative = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const onLinkedIn = useCallback(() => {
    if (!meta?.channels?.linkedin?.url) return;
    openExternal(meta.channels.linkedin.url);
    trackEvent('linkedin');
  }, [meta, openExternal, trackEvent]);

  const onX = useCallback(() => {
    if (!meta?.channels?.x?.url) return;
    openExternal(meta.channels.x.url);
    trackEvent('x');
  }, [meta, openExternal, trackEvent]);

  const onCopy = useCallback(async () => {
    if (!meta?.channels?.copy?.url) return;
    try {
      await navigator.clipboard.writeText(meta.channels.copy.url);
      setCopied(true);
      trackEvent('copy');
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      setError('Copie impossible — votre navigateur a refusé l\'accès au presse-papier.');
    }
  }, [meta, trackEvent]);

  if (error) {
    return (
      <div
        className="text-center mt-3 text-[11px] font-medium"
        style={{ color: '#C44B6F' }}
        data-testid="demo-share-error"
      >
        Partage indisponible : {error}
      </div>
    );
  }

  if (!meta) {
    return (
      <div
        className="flex items-center justify-center gap-1.5 mt-3 text-[11px]"
        style={{ color: T.inkSoft }}
        data-testid="demo-share-loading"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Chargement du partage…
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.85 }}
      className="flex flex-wrap items-center justify-center gap-2 mt-3"
      data-testid="demo-share-cluster"
    >
      {hasNative && (
        <ShareBtn
          icon={Share2}
          label="Partager"
          onClick={onNativeShare}
          testid="demo-share-native"
        />
      )}
      <ShareBtn
        icon={Linkedin}
        label="LinkedIn"
        onClick={onLinkedIn}
        testid="demo-share-linkedin"
      />
      <ShareBtn
        icon={Twitter}
        label="X (Twitter)"
        onClick={onX}
        testid="demo-share-x"
      />
      <ShareBtn
        icon={LinkIcon}
        label="Copier le lien"
        onClick={onCopy}
        copied={copied}
        testid="demo-share-copy"
      />
    </motion.div>
  );
}
